import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../config/firebaseConfig";
import { subscribeToStudents } from "../services/studentService";
import {
  getAllowedRoles,
  hasPermission,
  PERMISSIONS,
} from "../utils/roleAccess";

function buildOnboardingStudent(student) {
  return {
    uid: String(student.uid || "").trim(),
    name: student.name || "",
    className: student.className || "",
    phone: student.phone || "",
    qrImageUrl: student.qrImageUrl || "",
    activeqr: Boolean(student.activeqr),
  };
}

export default function StudentOnboardingQueue() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [canManageOnboarding, setCanManageOnboarding] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const currentYearUidPrefix = String(new Date().getFullYear()).slice(-2);

  useEffect(() => {
    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!active) {
        return;
      }

      try {
        if (!currentUser) {
          setUser(null);
          setCanManageOnboarding(false);
          return;
        }

        setUser(currentUser);
        const userReference = doc(db, "users", currentUser.uid);
        const userSnapshot = await getDoc(userReference);

        if (!active) {
          return;
        }

        setCanManageOnboarding(
          userSnapshot.exists() &&
            hasPermission(userSnapshot.data(), PERMISSIONS.STUDENT_ONBOARDING),
        );
      } finally {
        if (active) {
          setAuthLoading(false);
        }
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    if (!user || !canManageOnboarding) {
      setLoadingStudents(false);
      return undefined;
    }

    setLoadingStudents(true);

    const unsubscribe = subscribeToStudents(
      (studentRecords) => {
        setStudents(studentRecords);
        setLoadingStudents(false);
      },
      (error) => {
        console.error("Student onboarding queue error:", error);
        setLoadingStudents(false);
      },
    );

    return () => unsubscribe();
  }, [authLoading, canManageOnboarding, user]);

  const qrReadyStudents = useMemo(() => {
    const normalizedQuery = searchText.trim().toLowerCase();

    return students
      .filter((student) => student.activeqr !== true)
      .filter((student) => Boolean(String(student.qrImageUrl || "").trim()))
      .filter((student) =>
        String(student.uid || "")
          .trim()
          .toUpperCase()
          .startsWith(currentYearUidPrefix),
      )
      .filter((student) => {
        if (!normalizedQuery) {
          return true;
        }

        return (
          String(student.name || "")
            .toLowerCase()
            .includes(normalizedQuery) ||
          String(student.uid || "")
            .toLowerCase()
            .includes(normalizedQuery) ||
          String(student.className || "")
            .toLowerCase()
            .includes(normalizedQuery)
        );
      })
      .sort((first, second) =>
        String(first.name || "").localeCompare(String(second.name || "")),
      );
  }, [currentYearUidPrefix, searchText, students]);

  useEffect(() => {
    if (!qrReadyStudents.length) {
      setSelectedStudentId("");
      return;
    }

    setSelectedStudentId((currentValue) => {
      const stillExists = qrReadyStudents.some(
        (student) => student.id === currentValue,
      );

      return stillExists ? currentValue : qrReadyStudents[0].id;
    });
  }, [qrReadyStudents]);

  const selectedStudent = useMemo(
    () =>
      qrReadyStudents.find((student) => student.id === selectedStudentId) ||
      null,
    [qrReadyStudents, selectedStudentId],
  );

  if (authLoading || loadingStudents) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#ecfeff] via-[#eff6ff] to-[#f0fdfa] p-4">
        <div className="rounded-3xl border border-white/60 bg-white/90 px-8 py-7 text-center shadow-2xl backdrop-blur">
          <p className="text-base font-semibold text-[#155e75]">
            Loading onboarding queue...
          </p>
        </div>
      </div>
    );
  }

  if (!user || !canManageOnboarding) {
    const allowedRoles = getAllowedRoles(PERMISSIONS.STUDENT_ONBOARDING).join(
      ", ",
    );

    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#ecfeff] via-[#eff6ff] to-[#f0fdfa] px-4">
        <div className="max-w-md rounded-3xl border border-white/60 bg-white/90 p-8 text-center shadow-2xl backdrop-blur">
          <h1 className="text-2xl font-semibold text-[#0f172a]">
            Access required
          </h1>
          <p className="mt-3 text-sm leading-6 text-[#334155]">
            Users with these roles can open this onboarding queue:{" "}
            {allowedRoles || "admin"}.
          </p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="mt-6 rounded-xl bg-gradient-to-r from-[#0f766e] to-[#1d4ed8] px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:from-[#115e59] hover:to-[#1e40af]"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#ecfeff] via-[#eff6ff] to-[#fef9c3] px-4 py-6 sm:px-6 lg:px-8"
      style={{ fontFamily: '"Poppins", "Segoe UI", sans-serif' }}
    >
      <div className="mx-auto max-w-7xl">
        <div className="rounded-[2rem] border border-white/30 bg-gradient-to-r from-[#0f172a] via-[#134e4a] to-[#1d4ed8] p-7 text-white shadow-2xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-100">
                Student Onboarding Queue
              </p>

              <h1 className="mt-2 text-3xl font-semibold text-white sm:text-4xl">
                New Student Admission
              </h1>
            </div>

            <div className="flex flex-wrap gap-3">
              {/* <button
                type="button"
                onClick={() => navigate("/student-onboarding")}
                className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#0f172a] shadow-lg transition hover:bg-cyan-50"
              >
                Blank Onboarding Form
              </button> */}
              <button
                type="button"
                onClick={() => navigate("/admin-dashboard")}
                className="rounded-xl border border-white/30 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/20"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {/* <div className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur">
              <p className="text-sm text-cyan-100">Inactive students</p>
              <p className="mt-2 text-3xl font-semibold text-white">
                {inactiveCount}
              </p>
            </div> */}
            <div className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur">
              <p className="text-sm text-cyan-100">Remaing Qr codes</p>
              <p className="mt-2 text-3xl font-semibold text-white">
                {qrReadyStudents.length}
              </p>
            </div>
            {/* <div className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur">
              <p className="text-sm text-cyan-100">Missing QR URL</p>
              <p className="mt-2 text-3xl font-semibold text-white">
                {missingQrCount}
              </p>
            </div> */}
          </div>
        </div>

        <div className="mt-6 rounded-[2rem] border border-[#bae6fd] bg-white/90 p-6 shadow-2xl backdrop-blur">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2>Step 1</h2>
              <h2 className="text-2xl font-semibold text-[#0f172a]">
                Select Student From Dropdown (Optional)
              </h2>
              <p className="mt-1 text-sm text-[#475569]">
                Search by name, UID, or class, then onboard the selected
                student. Only inactive QR-ready UIDs starting with{" "}
                <span className="font-semibold text-[#0f766e]">
                  {currentYearUidPrefix}
                </span>{" "}
                are listed.
              </p>
            </div>

            <input
              type="text"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search by name, UID, or class"
              className="w-full rounded-xl border border-[#a5f3fc] bg-[#f0fdfa] px-4 py-3 text-sm text-[#0f172a] outline-none transition focus:border-[#14b8a6] focus:bg-white focus:ring-2 focus:ring-[#99f6e4] lg:max-w-sm"
            />
          </div>

          {!qrReadyStudents.length ? (
            <div className="mt-6 rounded-2xl border border-dashed border-[#bae6fd] bg-[#f8fafc] px-6 py-12 text-center">
              <p className="text-lg font-semibold text-[#0f172a]">
                No QR-ready inactive students found
              </p>
              <p className="mt-2 text-sm text-[#64748b]">
                Add `qrImageUrl` for inactive students with UID prefix{" "}
                <span className="font-semibold text-[#0f766e]">
                  {currentYearUidPrefix}
                </span>{" "}
                and they will appear in this dropdown.
              </p>
            </div>
          ) : (
            <div className="mt-6 grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
              <div className="rounded-2xl border border-[#a5f3fc] bg-gradient-to-br from-white to-[#ecfeff] p-5 shadow-lg">
                <select
                  value={selectedStudentId}
                  onChange={(event) => setSelectedStudentId(event.target.value)}
                  className="w-full rounded-xl border border-[#67e8f9] bg-white px-4 py-3 text-sm font-medium text-[#0f172a] outline-none transition focus:border-[#14b8a6] focus:ring-2 focus:ring-[#99f6e4]"
                >
                  {qrReadyStudents.map((student) => (
                    <option key={student.id} value={student.id}>
                      {`${student.name || "Unnamed"} | UID ${student.uid || "N/A"} | ${student.className || "Class N/A"}`}
                    </option>
                  ))}
                </select>

                <div className="mt-5 rounded-2xl border border-[#99f6e4] bg-white/80 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0f766e]">
                    Selected Student
                  </p>

                  <div className="mt-3 space-y-2 text-sm text-[#1e293b]">
                    <p>
                      <span className="font-semibold text-[#0f172a]">
                        Name:
                      </span>{" "}
                      {selectedStudent?.name || "Not available"}
                    </p>
                    <p>
                      <span className="font-semibold text-[#0f172a]">UID:</span>{" "}
                      {selectedStudent?.uid || "Not available"}
                    </p>
                    <p>
                      <span className="font-semibold text-[#0f172a]">
                        Class:
                      </span>{" "}
                      {selectedStudent?.className || "Not available"}
                    </p>
                    <p>
                      <span className="font-semibold text-[#0f172a]">
                        Phone:
                      </span>{" "}
                      {selectedStudent?.phone || "Not available"}
                    </p>
                  </div>

                  {selectedStudent?.qrImageUrl ? (
                    <div className="mt-4 rounded-xl border border-[#a5f3fc] bg-[#f0fdfa] p-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0f766e]">
                        QR Preview
                      </p>
                      <img
                        src={selectedStudent.qrImageUrl}
                        alt="Student QR"
                        className="mt-2 h-36 w-36 rounded-xl border border-[#67e8f9] bg-white p-2 object-contain"
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl border border-[#fde68a] bg-gradient-to-br from-[#fffbeb] to-white p-5 shadow-lg">
                <h2>Step 2</h2>

                <h3 className="mt-2 text-xl font-semibold text-[#0f172a]">
                  Open Onboarding Form
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#475569]">
                  Click below to load the selected student by URI with prefilled
                  details.
                </p>

                <button
                  type="button"
                  disabled={!selectedStudent}
                  onClick={() => {
                    if (!selectedStudent) {
                      return;
                    }

                    const uidValue = String(selectedStudent.uid || "").trim();
                    const targetPath = uidValue
                      ? `/student-onboarding/${encodeURIComponent(uidValue)}`
                      : "/student-onboarding";

                    navigate(targetPath, {
                      state: {
                        studentRecord: buildOnboardingStudent(selectedStudent),
                      },
                    });
                  }}
                  className={`mt-6 w-full rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-lg transition ${
                    selectedStudent
                      ? "bg-gradient-to-r from-[#0f766e] to-[#1d4ed8] hover:from-[#115e59] hover:to-[#1e40af]"
                      : "cursor-not-allowed bg-slate-400"
                  }`}
                >
                  Select And Onboard
                </button>

                {selectedStudent?.qrImageUrl ? (
                  <a
                    href={selectedStudent.qrImageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 block text-center text-sm font-semibold text-[#0f766e] underline-offset-4 hover:underline"
                  >
                    Open QR URL
                  </a>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
